import { __assign, __read } from "tslib";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { pluginLog } from './utils';
import { usePlotPluginContext } from './context';
import usePrevious from 'react-use/lib/usePrevious';
import useMountedState from 'react-use/lib/useMountedState';
export var usePlotPlugins = function () {
    /**
     * Map of registered plugins (via children)
     * Used to build uPlot plugins config
     */
    var _a = __read(useState({}), 2), plugins = _a[0], setPlugins = _a[1];
    // arePluginsReady determines whether or not all plugins has already registered and uPlot should be initialised
    var _b = __read(useState(false), 2), arePluginsReady = _b[0], setPluginsReady = _b[1];
    var cancellationToken = useRef();
    var isMounted = useRef(false);
    var checkPluginsReady = useCallback(function () {
        if (cancellationToken.current) {
            window.cancelAnimationFrame(cancellationToken.current);
            cancellationToken.current = undefined;
        }
        /**
         * After registering plugin let's wait for all code to complete to set arePluginsReady to true.
         * If any other plugin will try to register, the previously scheduled call will be canceled
         * and arePluginsReady will be deferred to next animation frame.
         */
        cancellationToken.current = window.requestAnimationFrame(function () {
            if (isMounted.current) {
                setPluginsReady(true);
            }
        });
    }, [cancellationToken, setPluginsReady]);
    var registerPlugin = useCallback(function (plugin) {
        pluginLog(plugin.id, false, 'register');
        setPlugins(function (plugs) {
            var _a;
            if (plugs.hasOwnProperty(plugin.id)) {
                throw new Error(plugin.id + " that is already registered");
            }
            return __assign(__assign({}, plugs), (_a = {}, _a[plugin.id] = plugin, _a));
        });
        checkPluginsReady();
        return function () {
            setPlugins(function (p) {
                pluginLog(plugin.id, false, 'unregister');
                delete p[plugin.id];
                return __assign({}, p);
            });
        };
    }, [setPlugins]);
    // When uPlot mounts let's check if there are any plugins pending registration
    useEffect(function () {
        isMounted.current = true;
        checkPluginsReady();
        return function () {
            isMounted.current = false;
            if (cancellationToken.current) {
                window.cancelAnimationFrame(cancellationToken.current);
            }
        };
    }, []);
    return {
        arePluginsReady: arePluginsReady,
        plugins: plugins || {},
        registerPlugin: registerPlugin,
    };
};
var paddingSide = function (u, side, sidesWithAxes, cycleNum) {
    var hasCrossAxis = side % 2 ? sidesWithAxes[0] || sidesWithAxes[2] : sidesWithAxes[1] || sidesWithAxes[3];
    return sidesWithAxes[side] || !hasCrossAxis ? 0 : 8;
};
export var DEFAULT_PLOT_CONFIG = {
    focus: {
        alpha: 1,
    },
    cursor: {
        focus: {
            prox: 30,
        },
    },
    legend: {
        show: false,
    },
    padding: [paddingSide, paddingSide, paddingSide, paddingSide],
    series: [],
    hooks: {},
};
export var usePlotConfig = function (width, height, configBuilder) {
    var _a = usePlotPlugins(), arePluginsReady = _a.arePluginsReady, plugins = _a.plugins, registerPlugin = _a.registerPlugin;
    var _b = __read(useState(false), 2), isConfigReady = _b[0], setIsConfigReady = _b[1];
    var currentConfig = useRef();
    useLayoutEffect(function () {
        if (!arePluginsReady) {
            return;
        }
        currentConfig.current = __assign(__assign(__assign({}, DEFAULT_PLOT_CONFIG), { width: width,
            height: height, ms: 1, plugins: Object.entries(plugins).map(function (p) { return ({
                hooks: p[1].hooks,
            }); }) }), configBuilder.getConfig());
        setIsConfigReady(true);
    }, [arePluginsReady, plugins, width, height, configBuilder]);
    return {
        isConfigReady: isConfigReady,
        registerPlugin: registerPlugin,
        currentConfig: currentConfig,
    };
};
/**
 * Forces re-render of a component when uPlots's draw hook is fired.
 * This hook is usefull in scenarios when you want to reposition XYCanvas elements when i.e. plot size changes
 * @param pluginId - id under which the plugin will be registered
 */
export var useRefreshAfterGraphRendered = function (pluginId) {
    var pluginsApi = usePlotPluginContext();
    var isMounted = useMountedState();
    var _a = __read(useState(0), 2), renderToken = _a[0], setRenderToken = _a[1];
    useEffect(function () {
        var unregister = pluginsApi.registerPlugin({
            id: pluginId,
            hooks: {
                // refresh events when uPlot draws
                draw: function () {
                    if (isMounted()) {
                        setRenderToken(function (c) { return c + 1; });
                    }
                    return;
                },
            },
        });
        return function () {
            unregister();
        };
    }, []);
    return renderToken;
};
export function useRevision(dep, cmp) {
    var _a = __read(useState(0), 2), rev = _a[0], setRev = _a[1];
    var prevDep = usePrevious(dep);
    var comparator = cmp ? cmp : function (a, b) { return a === b; };
    useLayoutEffect(function () {
        var hasChange = !comparator(prevDep, dep);
        if (hasChange) {
            setRev(function (r) { return r + 1; });
        }
    }, [dep]);
    return rev;
}
//# sourceMappingURL=hooks.js.map